/**
 * Curated DRONE FEED source registry (#216 — drone feed as a media-tile kind).
 *
 * A drone feed is an AMBIENT, operator-gated media-tile kind — exactly like the
 * news reel (#182): NOT a map marker, NOT an anomaly, and NOT a default-view layer
 * (design council). An operator pins a drone feed onto a workstation (the
 * `kind:'drone'` pinned-feed path) or it rides the WALL media mosaic; precision
 * (does this feed matter?) lives at promotion, never here. So this module is a
 * curated list plus the pure selection/lookup helpers — no scoring, no Response
 * Level. It is the drone twin of `news-source.ts` and shares its shape.
 *
 * ── SOURCE MODEL (read this) ──────────────────────────────────────────────────
 * There is NO live drone-video INGEST in the platform today. The existing "drone"
 * code (`drone-coverage` / `response-assets`) models a drone provider as a
 * COVERAGE-AND-RESPONSE-TIME asset (drive + flight radius, ETA) — it does not
 * carry a live video stream. So, per the build directive, this registry is a
 * CURATED / MANUAL STUB: a small hand-maintained list of drone-feed sources that
 * resolves through the ONLY live-video path the platform already has — the shared
 * YouTube channel-live resolver (`resolveChannelLiveVideoId`, reused by the camera
 * + news paths). Each source therefore points at a YouTube *channel* (UC…), NOT a
 * pinned video id, so the current live broadcast is resolved AT VIEW TIME and a
 * pinned id can never rot.
 *
 * The channel ids below are PLACEHOLDERS for a real agency / partner drone feed —
 * when a source is not currently live the view-time resolver returns null and the
 * shared player surfaces an honest "not live" state (it never crashes the tile),
 * the same graceful behaviour as a news outlet that is off-air. A real drone
 * integration (an agency RTMP/HLS relay, a partner drone provider's stream) would
 * replace these entries — or extend the model with a direct-stream field — without
 * touching the pin / panel / wall plumbing this registry feeds.
 *
 * Personal workstation state: pinning a drone feed is the `kind:'drone'` pinned-
 * feed path (localStorage + BroadcastChannel) — NO operator_actions verb, NO
 * migration (mirrors camera + news pins).
 */

/** Operational category buckets used to group + filter the picker. */
export type DroneCategory = 'wildfire' | 'flood' | 'sar' | 'general';

export interface DroneSource {
  /** Stable slug id — the pinned-feed id AND the `/api/drone-feeds/:id/youtube`
   *  param. Lowercase kebab; never a YouTube id (those rotate per rebrand). */
  id: string;
  /** Operator / agency running the feed, e.g. "BC Wildfire Service". */
  operator: string;
  /** Operational category for grouping + the picker's category filter. */
  category: DroneCategory;
  /** Display label for the category group header. */
  categoryLabel: string;
  /** Tile / pin caption (operator + a "Drone" suffix). */
  label: string;
  /** YouTube channel id (UC + 22 chars). Resolved to the current live videoId at
   *  view time — never a pinned video id. Placeholder until a real drone feed is
   *  wired (see module docblock). */
  channelId: string;
  /** Optional title-match keyword — set ONLY when a channel runs >1 simultaneous
   *  live and the right one must be disambiguated by title (camera-path semantics).
   *  Omitted for a single dedicated drone live. */
  titleMatch?: string;
}

const CATEGORY_LABELS: Record<DroneCategory, string> = {
  wildfire: 'Wildfire',
  flood: 'Flood',
  sar: 'Search & Rescue',
  general: 'General / Multi-Hazard',
};

/** Display order for the category groups in the picker (wildfire first — the
 *  primary hazard arc). */
export const DRONE_CATEGORY_ORDER: DroneCategory[] = ['wildfire', 'flood', 'sar', 'general'];

function source(
  id: string,
  operator: string,
  category: DroneCategory,
  channelId: string,
  titleMatch?: string,
): DroneSource {
  return {
    id,
    operator,
    category,
    categoryLabel: CATEGORY_LABELS[category],
    label: `${operator} · Drone`,
    channelId,
    ...(titleMatch ? { titleMatch } : {}),
  };
}

/**
 * The curated STUB registry (see module docblock). Channel ids are PLACEHOLDERS
 * for a real agency / partner drone relay — a not-live source resolves to null and
 * the player shows an honest "not live" state. Keep this list short; replace ids
 * (or extend the model with a direct-stream field) when a real drone feed lands.
 */
export const DRONE_SOURCES: readonly DroneSource[] = [
  // Wildfire recon — aerial fire-line / hotspot overwatch.
  source('wildfire-recon-1', 'Wildfire Recon Drone 1', 'wildfire', 'UCdroneWildfireRecon1___'),
  source('wildfire-recon-2', 'Wildfire Recon Drone 2', 'wildfire', 'UCdroneWildfireRecon2___'),
  // Flood survey — riverine / inundation mapping overflights.
  source('flood-survey-1', 'Flood Survey Drone 1', 'flood', 'UCdroneFloodSurvey1_____'),
  // Search & rescue — wide-area aerial search.
  source('sar-overwatch-1', 'SAR Overwatch Drone 1', 'sar', 'UCdroneSarOverwatch1____'),
  // General / multi-hazard — a roaming all-hazard aerial asset.
  source('general-aerial-1', 'General Aerial Drone 1', 'general', 'UCdroneGeneralAerial1___'),
];

/** Look up a source by its stable slug id (the pin id / route param). */
export function findDroneSource(id: string): DroneSource | undefined {
  return DRONE_SOURCES.find((s) => s.id === id);
}

export interface DroneSourceFilter {
  /** Restrict to a single category bucket. */
  category?: DroneCategory;
  /** Case-insensitive substring match on the operator name. */
  operator?: string;
}

/**
 * Pure selection: filter the registry by operator substring and/or category.
 * Returns a fresh array (registry order preserved) — empty when nothing matches.
 * No network, no scoring; this is just operator-facing browse/filter.
 */
export function selectDroneSources(filter: DroneSourceFilter = {}): DroneSource[] {
  const operator = filter.operator?.trim().toLowerCase();
  return DRONE_SOURCES.filter((s) => {
    if (filter.category && s.category !== filter.category) return false;
    if (operator && !s.operator.toLowerCase().includes(operator)) return false;
    return true;
  });
}

export interface DroneCategoryGroup {
  category: DroneCategory;
  categoryLabel: string;
  sources: DroneSource[];
}

/**
 * Group sources by category in {@link DRONE_CATEGORY_ORDER} (then any extra
 * buckets), dropping empty groups. Drives the picker's grouped list. Honours a
 * filter so the grouped view and the flat {@link selectDroneSources} stay
 * consistent.
 */
export function groupDroneSourcesByCategory(filter: DroneSourceFilter = {}): DroneCategoryGroup[] {
  const selected = selectDroneSources(filter);
  const order = [
    ...DRONE_CATEGORY_ORDER,
    ...selected.map((s) => s.category).filter((c) => !DRONE_CATEGORY_ORDER.includes(c)),
  ];
  const seen = new Set<DroneCategory>();
  const groups: DroneCategoryGroup[] = [];
  for (const category of order) {
    if (seen.has(category)) continue;
    seen.add(category);
    const sources = selected.filter((s) => s.category === category);
    if (sources.length === 0) continue;
    groups.push({ category, categoryLabel: CATEGORY_LABELS[category], sources });
  }
  return groups;
}

/**
 * Shape a source into the `kind:'drone'` pinned-feed the panel/wall consume. Kept
 * here (not in the web pin module) so the pin id↔registry contract lives with the
 * registry. Mirrors the camera/news pin shape: { kind, id, label }.
 */
export function droneSourceToPinnedFeed(source: DroneSource): {
  kind: 'drone';
  id: string;
  label: string;
} {
  return { kind: 'drone', id: source.id, label: source.label };
}
